import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Link } from "wouter";
import image468 from "../assets/468.jpg";
import image545 from "../assets/545.jpg";

interface HeroSectionProps {
  onExploreClick: () => void;
  onContactClick: () => void;
}

const HeroSection = ({ onExploreClick, onContactClick }: HeroSectionProps) => {
  const [activeSlide, setActiveSlide] = useState(0);

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev === 0 ? 1 : 0));
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(interval);
  }, []);

  const slides = [
    {
      image: image468,
      title: "Premium Welding Services in Ridgefield, CT",
      description:
        "Crafting excellence in metal. From decorative railings to structural solutions, we bring precision, creativity, and expertise to every project.",
      buttons: [
        {
          text: "Our Services",
          onClick: onExploreClick,
          className:
            "bg-black/50 hover:bg-black/70 text-white border-2 border-white",
        },
        {
          text: "Contact Us",
          onClick: onContactClick,
          className:
            "bg-black/50 hover:bg-black/70 text-white border-2 border-white",
        },
      ],
    },
    {
      image: image545,
      title: "Exceptional Craftsmanship & Attention to Detail",
      description:
        "Explore our portfolio of custom metalwork projects showcasing superior quality and craftsmanship that transforms ordinary spaces into extraordinary environments.",
      buttons: [
        {
          text: "Check Our Work",
          link: "/gallery",
          className:
            "bg-black hover:bg-red-600 text-white border-2 border-white",
        },
        {
          text: "Get a Quote",
          onClick: onContactClick,
          className:
            "bg-transparent hover:bg-white hover:text-black text-white border-2 border-white",
        },
      ],
    },
  ];

  return (
    <section
      id="home"
      className="relative h-[90vh] flex items-center bg-primary overflow-hidden"
    >
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 z-0 transition-opacity duration-1000 ${activeSlide === index ? "opacity-100" : "opacity-0"}`}
        >
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <img
            src={slide.image}
            alt={`Slide ${index + 1}`}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      ))}

      {slides.map((slide, index) => (
        <div
          key={index}
          className={`container mx-auto px-4 z-10 transition-opacity duration-1000 absolute inset-0 flex items-center ${activeSlide === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={
              activeSlide === index
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.7 }}
          >
            <h1 className="text-4xl md:text-6xl font-montserrat font-bold text-white leading-tight mb-6 text-shadow-lg">
              {slide.title}
            </h1>
            <p className="text-xl text-white mb-8 text-shadow-md">
              {slide.description}
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              {slide.buttons.map((button, buttonIndex) =>
                button.link ? (
                  <Link key={buttonIndex} href={button.link}>
                    <motion.div
                      className={`text-lg font-montserrat font-semibold py-3 px-10 rounded-md transition-all shadow-lg hover:shadow-xl flex items-center justify-center cursor-pointer ${button.className}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {button.text}
                    </motion.div>
                  </Link>
                ) : (
                  <motion.button
                    key={buttonIndex}
                    onClick={button.onClick}
                    className={`text-lg font-montserrat font-semibold py-3 px-10 rounded-md transition-all shadow-lg hover:shadow-xl flex items-center justify-center ${button.className}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {button.text}
                  </motion.button>
                ),
              )}
            </div>
          </motion.div>
        </div>
      ))}

      {/* Slide indicators */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveSlide(index)}
            className={`w-3 h-3 rounded-full transition-all shadow-md ${activeSlide === index ? "bg-white scale-125" : "bg-gray-400"}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
      >
        <button
          onClick={onExploreClick}
          className="text-white text-3xl opacity-80 hover:opacity-100 transition-opacity shadow-lg p-2 rounded-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </button>
      </motion.div>
    </section>
  );
};

export default HeroSection;
